<template>
  <vue-final-modal
    classes="flex justify-center items-center"
    v-model="modalVisible"
  >
    <div class="im-modal bg-white rounded-xl border border-gray-200 shadow-2xl mx-4 flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="im-header flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200 flex-shrink-0">
        <div class="flex-1 min-w-0">
          <h3 class="im-title m-0 flex items-center text-base font-semibold text-gray-800 leading-tight">
            <svg
              class="im-title-icon mr-2 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
              <polyline points="14 2 14 7 19 7" />
              <path d="M3 12h8" />
              <path d="M8 8l4 4-4 4" />
            </svg>
            Import Version
          </h3>
          <p class="im-subtitle text-xs text-gray-500 mt-1 mb-0">
            <span v-if="step === 'upload'">Step 1 of 2 — Select file</span>
            <span v-else>Step 2 of 2 — Configure import</span>
          </p>
        </div>
        <button class="im-close" @click="handleClose" aria-label="Close">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Step 1: Upload -->
      <div
        v-if="step === 'upload'"
        class="im-body im-body-upload p-5 flex flex-col items-stretch justify-center bg-white"
      >
        <div
          @dragover.prevent="dragStarted"
          @dragleave.prevent="dragLeave"
          @drop.prevent="onDrop"
          class="im-dropzone flex items-center justify-center"
          :class="{ 'im-dropzone-active': isShowDragImage, 'im-dropzone-has-file': !!selectedFile }"
        >
          <div v-if="!selectedFile" class="flex flex-col items-center p-6 text-center">
            <div class="im-dropzone-icon-wrap mb-3 flex items-center justify-center">
              <svg
                class="im-dropzone-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
                <polyline points="14 2 14 7 19 7" />
                <path d="M3 12h8" />
                <path d="M8 8l4 4-4 4" />
              </svg>
            </div>
            <p class="text-sm font-medium text-gray-800 m-0 mb-1">
              {{ isShowDragImage ? 'Release to import' : 'Drag & drop file to import' }}
            </p>
            <p class="text-xs text-gray-500 m-0 mb-4">JSON files only</p>
            <button @click="openFileUploadPopup" class="im-btn im-btn-outline">Browse files</button>
          </div>
          <div v-else class="flex flex-col items-center p-6 text-center">
            <i class="fa-solid fa-file-code im-file-icon mb-2" aria-hidden="true"></i>
            <p class="text-sm font-medium text-gray-800 m-0 mb-3 break-all">{{ selectedFile.name }}</p>
            <button @click="openFileUploadPopup" class="im-btn im-btn-ghost">Choose different file</button>
          </div>
          <input type="file" ref="fileInput" @change="onBrowse" hidden accept=".json" />
        </div>
        <p class="im-help mt-3 text-sm text-gray-500 text-center">
          Import versions from an exported JSON file.
        </p>
      </div>

      <!-- Step 2: Configure -->
      <div v-else class="im-body p-5 bg-white">
        <!-- Selected file -->
        <div class="bg-white border border-gray-200 rounded-lg px-3 py-2 mb-4">
          <div class="flex items-center text-sm text-gray-800">
            <i class="fa-solid fa-file-code im-context-icon mr-2" aria-hidden="true"></i>
            <span class="font-medium break-all">{{ selectedFile && selectedFile.name }}</span>
          </div>
        </div>

        <!-- Version list -->
        <div class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <span class="im-section-label text-xs font-semibold text-gray-500 uppercase">Versions to import</span>
            <label class="flex items-center text-xs text-gray-500 cursor-pointer select-none">
              <input
                type="checkbox"
                class="im-checkbox flex-shrink-0 mr-2"
                :checked="allSelected"
                @change="toggleSelectAll"
              />
              <span>Select all</span>
            </label>
          </div>

          <div class="im-version-list border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div
              v-for="row in rows"
              :key="row.original"
              class="im-version-row flex items-start px-3 py-2 border-b border-gray-200"
              :class="{ 'im-version-row-selected': row.selected, 'im-version-row-dimmed': !row.selected }"
            >
              <label class="cursor-pointer flex-shrink-0 mr-3 pt-1">
                <input
                  type="checkbox"
                  class="im-checkbox"
                  v-model="row.selected"
                  @change="recomputeSuggestions"
                />
              </label>

              <div class="flex-1 min-w-0">
                <div class="flex items-center flex-wrap">
                  <span class="text-sm font-semibold text-gray-800 mr-2">v{{ row.original }}</span>
                  <span v-if="row.is_current" class="im-tag mr-1">current</span>
                  <span v-if="row.is_published" class="im-tag">published</span>
                </div>
                <p
                  v-if="row.clash"
                  class="im-warn text-xs text-gray-500 italic mt-1 mb-0"
                  :class="{ 'invisible': !row.selected }"
                >
                  Adjusted to stay higher than existing versions
                </p>
              </div>

              <div class="flex flex-col items-end flex-shrink-0 ml-2">
                <label class="im-rename-label text-xs text-gray-500 uppercase">Import as</label>
                <input
                  type="text"
                  v-model.trim="row.finalVersion"
                  :disabled="!row.selected"
                  @input="onFinalVersionEdit(row)"
                  class="im-rename-input text-center text-sm font-medium"
                  :class="{ 'im-rename-input-error': row.selected && !isValidVersion(row.finalVersion) }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Meta: current / published -->
        <div class="bg-white border border-gray-200 rounded-lg px-3 py-3">
          <p class="im-section-label text-xs font-semibold text-gray-500 uppercase m-0 mb-3">After import</p>

          <!-- Single version: checkboxes -->
          <div v-if="isSingleVersionImport" class="flex flex-col">
            <label
              class="im-meta-option flex items-center px-2 py-2 rounded-md border mb-2"
              :class="{ 'im-meta-option-checked': setAsCurrent }"
            >
              <input
                type="checkbox"
                class="im-checkbox flex-shrink-0 mr-2"
                v-model="setAsCurrent"
                :disabled="selectedCount === 0"
              />
              <span class="text-sm text-gray-800 flex-1">Set as current</span>
              <span class="im-meta-value text-xs font-semibold flex-shrink-0">{{ soleImportedVersion || '—' }}</span>
            </label>
            <label
              class="im-meta-option flex items-center px-2 py-2 rounded-md border"
              :class="{ 'im-meta-option-checked': setAsPublished }"
            >
              <input
                type="checkbox"
                class="im-checkbox flex-shrink-0 mr-2"
                v-model="setAsPublished"
                :disabled="selectedCount === 0"
              />
              <span class="text-sm text-gray-800 flex-1">Set as published</span>
              <span class="im-meta-value text-xs font-semibold flex-shrink-0">{{ soleImportedVersion || '—' }}</span>
            </label>
          </div>

          <!-- Multiple versions: dropdowns -->
          <div v-else class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Set as current</label>
              <select
                v-model="setCurrent"
                class="im-select w-full text-sm"
                :disabled="importedVersionOptions.length === 0"
              >
                <option value="">Leave unchanged</option>
                <option v-for="v in importedVersionOptions" :key="`cur-${v}`" :value="v">{{ v }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Set as published</label>
              <select
                v-model="setPublished"
                class="im-select w-full text-sm"
                :disabled="importedVersionOptions.length === 0"
              >
                <option value="">Leave unchanged</option>
                <option v-for="v in importedVersionOptions" :key="`pub-${v}`" :value="v">{{ v }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="im-footer flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
        <div class="flex-1 min-w-0 flex items-center">
          <button
            v-if="step === 'configure'"
            type="button"
            @click="backToUpload"
            class="im-btn im-btn-ghost"
          >
            <i class="fa-solid fa-arrow-left mr-1" aria-hidden="true"></i>
            Back
          </button>
        </div>
        <div class="flex items-center justify-end flex-shrink-0">
          <button
            type="button"
            @click="handleClose"
            class="im-btn im-btn-secondary mr-2"
          >
            Cancel
          </button>
          <button
            v-if="step === 'configure'"
            type="button"
            @click="confirmImport"
            :disabled="loading || selectedCount === 0"
            class="im-btn im-btn-primary"
          >
            <span v-if="loading" class="im-spinner mr-1"></span>
            <span v-else>
              Import {{ selectedCount }} version{{ selectedCount !== 1 ? 's' : '' }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </vue-final-modal>
</template>

<script>
import * as siteService from "../service/site";

const VERSION_REGEX = /^\d+(\.\d+)*$/;

export default {
  name: "ImportVersionModal",
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    uri: {
      type: String,
      required: true,
    },
    versionData: {
      type: Object,
      default: () => ({ versions: [], current_version: "", published_version: "" }),
    },
  },
  data() {
    return {
      step: "upload",
      isShowDragImage: false,
      selectedFile: null,
      loading: false,
      rows: [],
      setAsCurrent: false,
      setAsPublished: false,
      setCurrent: "",
      setPublished: "",
    };
  },
  computed: {
    modalVisible: {
      get() {
        return this.isVisible;
      },
      set(value) {
        this.$emit("update:isVisible", value);
        if (!value) this.$emit("close");
      },
    },
    existingVersions() {
      return (this.versionData && this.versionData.versions) || [];
    },
    selectedRows() {
      return this.rows.filter((r) => r.selected);
    },
    selectedCount() {
      return this.selectedRows.length;
    },
    allSelected() {
      return this.rows.length > 0 && this.rows.every((r) => r.selected);
    },
    isSingleVersionImport() {
      return this.rows.length === 1;
    },
    soleImportedVersion() {
      if (!this.isSingleVersionImport || !this.selectedRows.length) return null;
      return this.selectedRows[0].finalVersion || null;
    },
    importedVersionOptions() {
      const finals = this.selectedRows.map((r) => r.finalVersion).filter(Boolean);
      return this.sortVersions([...new Set(finals)]);
    },
    /** Highest version currently on the target site (null if none). */
    maxExistingVersion() {
      if (!this.existingVersions.length) return null;
      return this.getMaxVersion([...this.existingVersions]);
    },
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetState();
      }
    },
  },
  methods: {
    isValidVersion(v) {
      return typeof v === "string" && VERSION_REGEX.test(v.trim());
    },

    sortVersions(versions) {
      return [...versions].sort((a, b) => {
        const aParts = String(a).split(".").map(Number);
        const bParts = String(b).split(".").map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
      });
    },

    compareVersions(a, b) {
      const aParts = String(a).split(".").map(Number);
      const bParts = String(b).split(".").map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aVal = aParts[i] || 0;
        const bVal = bParts[i] || 0;
        if (aVal !== bVal) return aVal - bVal;
      }
      return 0;
    },

    incrementMinorOnMaxMajor(taken) {
      const takenSet = new Set(taken.map((v) => String(v)));
      const max = this.getMaxVersion(taken);
      const major = Number(String(max).split(".")[0]);

      let maxMinor = 0;
      taken.forEach((v) => {
        const parts = String(v).split(".").map(Number);
        if (parts[0] === major) {
          const minor = parts[1] || 0;
          if (minor > maxMinor) maxMinor = minor;
        }
      });

      let minor = maxMinor + 1;
      let candidate = `${major}.${minor}`;
      while (takenSet.has(candidate)) {
        minor += 1;
        candidate = `${major}.${minor}`;
      }
      return candidate;
    },

    getMaxVersion(versions) {
      let max = null;
      (versions || []).forEach((v) => {
        if (max === null || this.compareVersions(v, max) > 0) max = String(v);
      });
      return max;
    },

    generateHigherVersion(requested, taken) {
      const list = taken.map((v) => String(v));
      const base = String(requested);
      const takenSet = new Set(list);
      const max = this.getMaxVersion(list);

      if (max === null) return base;

      const cmp = this.compareVersions(base, max);

      if (cmp > 0 && !takenSet.has(base)) return base;

      return this.incrementMinorOnMaxMajor(list);
    },

    dragStarted() {
      this.isShowDragImage = true;
    },
    dragLeave() {
      this.isShowDragImage = false;
    },
    onDrop(event) {
      this.isShowDragImage = false;
      this.handleFile(event.dataTransfer.files[0]);
    },
    onBrowse(event) {
      this.handleFile(event.target.files[0]);
    },
    openFileUploadPopup() {
      this.$refs.fileInput.click();
    },

    async handleFile(file) {
      if (!file) return;
      if (file.type !== "application/json") {
        this.$notify({ type: "warn", text: "Please select a valid .json file." });
        return;
      }
      this.selectedFile = file;
      try {
        const jsonData = await this.parseJsonFile(file);
        this.validateJsonStructure(jsonData);
        const versions = this.extractVersions(jsonData);
        if (!versions.length) {
          throw new Error("No versions found in the uploaded file.");
        }
        this.buildRows(versions);
        this.step = "configure";
      } catch (err) {
        this.selectedFile = null;
        this.$notify({
          type: "error",
          text: err.message || "Invalid JSON file. Please check the file format.",
        });
      }
    },

    parseJsonFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            resolve(JSON.parse(e.target.result));
          } catch (err) {
            reject(new Error("Failed to parse JSON file. Invalid JSON format."));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsText(file);
      });
    },

    validateJsonStructure(jsonData) {
      if (!jsonData || typeof jsonData !== "object") {
        throw new Error("Invalid JSON structure. Expected an object.");
      }
      const hasSitesArray = jsonData.sites && Array.isArray(jsonData.sites);
      const hasSingleSite = jsonData.site && typeof jsonData.site === "object";
      if (!hasSitesArray && !hasSingleSite) {
        throw new Error("Invalid JSON structure. Missing 'sites' array or 'site' object.");
      }
      if (hasSitesArray && jsonData.sites.length === 0) {
        throw new Error("No site data found in the JSON file.");
      }
    },

    extractVersions(jsonData) {
      const versionsMap = new Map();
      const currentVersion = jsonData.siteversion && jsonData.siteversion.current_version;
      const publishedVersion = jsonData.siteversion && jsonData.siteversion.published_version;

      const add = (version) => {
        if (version && !versionsMap.has(version)) {
          versionsMap.set(version, {
            version,
            is_current: version === currentVersion,
            is_published: version === publishedVersion,
          });
        }
      };

      if (jsonData.sites && Array.isArray(jsonData.sites)) {
        jsonData.sites.forEach((site) => add(site.version));
      }
      if (jsonData.site && typeof jsonData.site === "object") {
        add(jsonData.site.version);
      }
      return Array.from(versionsMap.values());
    },

    buildRows(versions) {
      const ordered = this.sortVersions(versions.map((v) => v.version));
      const meta = new Map(versions.map((v) => [v.version, v]));
      const taken = [...this.existingVersions];
      this.rows = ordered.map((version) => {
        const finalVersion = this.generateHigherVersion(version, taken);
        taken.push(finalVersion);
        const info = meta.get(version) || {};
        return {
          original: version,
          selected: true,
          finalVersion,
          clash: finalVersion !== version,
          edited: false,
          is_current: !!info.is_current,
          is_published: !!info.is_published,
        };
      });
    },

    onFinalVersionEdit(row) {
      row.edited = true;
      this.pruneMetaSelections();
    },

    recomputeSuggestions() {
      const taken = [...this.existingVersions];
      this.rows.forEach((row) => {
        if (!row.selected) {
          row.clash = this.existingVersions.includes(row.original);
          return;
        }
        if (!row.edited) {
          row.finalVersion = this.generateHigherVersion(row.original, taken);
        }
        row.clash = row.finalVersion !== row.original;
        taken.push(row.finalVersion);
      });
      this.pruneMetaSelections();
    },

    pruneMetaSelections() {
      if (!this.isSingleVersionImport) {
        if (this.setCurrent && !this.importedVersionOptions.includes(this.setCurrent)) {
          this.setCurrent = "";
        }
        if (this.setPublished && !this.importedVersionOptions.includes(this.setPublished)) {
          this.setPublished = "";
        }
      }
    },

    toggleSelectAll() {
      const next = !this.allSelected;
      this.rows.forEach((row) => {
        row.selected = next;
      });
      this.recomputeSuggestions();
    },

    validateBeforeImport() {
      if (this.selectedCount === 0) {
        this.$notify({ type: "warn", text: "Please select at least one version to import." });
        return false;
      }
      const finals = [];
      for (const row of this.selectedRows) {
        const v = (row.finalVersion || "").trim();
        if (!this.isValidVersion(v)) {
          this.$notify({ type: "warn", text: `"${v || "(empty)"}" is not a valid version (numbers only, e.g. 1.2).` });
          return false;
        }
        if (this.existingVersions.includes(v)) {
          this.$notify({ type: "warn", text: `Version ${v} already exists on this site. Choose a different number.` });
          return false;
        }
        if (finals.includes(v)) {
          this.$notify({ type: "warn", text: `Version ${v} is used for more than one import. Each must be unique.` });
          return false;
        }
        if (this.maxExistingVersion && this.compareVersions(v, this.maxExistingVersion) <= 0) {
          this.$notify({
            type: "warn",
            text: `Version ${v} must be higher than the current highest version (${this.maxExistingVersion}).`,
          });
          return false;
        }
        finals.push(v);
      }
      return true;
    },

    async confirmImport() {
      if (!this.validateBeforeImport()) return;

      const formData = new FormData();
      formData.append("file", this.selectedFile);

      const mapping = {};
      this.selectedRows.forEach((row) => {
        formData.append("versions", row.original);
        mapping[row.original] = row.finalVersion.trim();
      });
      formData.append("versionMapping", JSON.stringify(mapping));

      const currentToSet = this.isSingleVersionImport
        ? (this.setAsCurrent ? this.soleImportedVersion : null)
        : (this.setCurrent || null);
      const publishedToSet = this.isSingleVersionImport
        ? (this.setAsPublished ? this.soleImportedVersion : null)
        : (this.setPublished || null);

      if (currentToSet) formData.append("setCurrent", currentToSet);
      if (publishedToSet) formData.append("setPublished", publishedToSet);

      this.loading = true;
      try {
        const response = await siteService.importSiteVersion(this.uri, formData);
        if (response && response.success === false) {
          this.$notify({ type: "error", text: response.error || "Error while importing version." });
          this.loading = false;
          return;
        }
        this.$notify(response.message || "Version imported successfully.");
        this.$emit("imported", {
          importedVersions: (response && response.importedVersions) || [],
          setCurrent: currentToSet,
          setPublished: publishedToSet,
        });
        this.loading = false;
        this.modalVisible = false;
      } catch (err) {
        this.loading = false;
        this.$notify({
          type: "error",
          text: err.error || err.message || "Error while importing version.",
        });
      }
    },

    backToUpload() {
      this.step = "upload";
      this.selectedFile = null;
      this.rows = [];
      this.setAsCurrent = false;
      this.setAsPublished = false;
      this.setCurrent = "";
      this.setPublished = "";
    },

    handleClose() {
      this.modalVisible = false;
    },

    resetState() {
      this.step = "upload";
      this.isShowDragImage = false;
      this.selectedFile = null;
      this.loading = false;
      this.rows = [];
      this.setAsCurrent = false;
      this.setAsPublished = false;
      this.setCurrent = "";
      this.setPublished = "";
    },
  },
};
</script>

<style scoped>
/*
  This project uses Tailwind 2 (no JIT), so arbitrary utilities like w-[520px]
  or bg-[#00aeef] are silently dropped. We use Tailwind for layout/colors that
  it supports out of the box, and scoped CSS for: brand color (#00aeef), exact
  pixel sizes, custom checkbox checkmark, scrollbar, and section-label sizing.
*/

.im-modal {
  width: 520px;
  max-width: calc(100vw - 32px);
  min-height: 420px;
  max-height: 90vh;
  font-family: "Inter", sans-serif;
}

.im-title-icon {
  width: 14px;
  height: 14px;
  color: #00aeef;
}

.im-close {
  background: none;
  border: none;
  color: #777;
  font-size: 16px;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  line-height: 1;
  transition: color 0.15s, background 0.15s;
}
.im-close:hover {
  color: #00aeef;
  background: #f5fbfe;
}

.im-body {
  overflow-y: auto;
  flex: 1 1 auto;
  width: 100%;
  box-sizing: border-box;
}
.im-body-upload {
  min-height: 320px;
}

.im-help {
  width: 100%;
}

/* Drop zone */
.im-dropzone {
  width: 100%;
  min-height: 280px;
  border: 2px dashed rgba(0, 174, 239, 0.25);
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}
.im-dropzone-active {
  border-color: #00aeef;
  background: #f5fbfe;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}
.im-dropzone-has-file {
  border-style: solid;
  border-color: rgba(0, 174, 239, 0.25);
  background: #f5fbfe;
}
.im-dropzone-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #e8f7fd;
}
.im-dropzone-icon {
  width: 22px;
  height: 22px;
  color: #00aeef;
}
.im-file-icon {
  font-size: 36px;
  color: #00aeef;
}

/* Context icon */
.im-context-icon {
  color: #00aeef;
  font-size: 13px;
  width: 16px;
  text-align: center;
}

/* Section label — Tailwind 2 has no tracking-[0.04em] without JIT */
.im-section-label {
  font-size: 11px;
  letter-spacing: 0.04em;
}

/* Version list */
.im-version-list {
  max-height: 220px;
  overflow-y: auto;
}
.im-version-list::-webkit-scrollbar {
  width: 4px;
}
.im-version-list::-webkit-scrollbar-track {
  background: #f5fbfe;
}
.im-version-list::-webkit-scrollbar-thumb {
  background: #00aeef;
  border-radius: 2px;
  opacity: 0.5;
}

.im-version-row {
  transition: background 0.15s;
}
.im-version-row:last-child {
  border-bottom: none;
}
.im-version-row-selected {
  background: #f5fbfe;
}
.im-version-row-dimmed {
  opacity: 0.5;
}

.im-tag {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 7px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: #e8f7fd;
  color: #00aeef;
}

.im-warn {
  min-height: 16px;
}

.im-rename-label {
  font-size: 9px;
  letter-spacing: 0.03em;
}
.im-rename-input {
  width: 72px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 4px 8px;
  outline: none;
  background: #fff;
  color: #333;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.im-rename-input:focus {
  border-color: #00aeef;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.15);
}
.im-rename-input-error {
  border-color: #00aeef;
}
.im-rename-input:disabled {
  background: #fafafa;
  color: #777;
}

/* Meta options */
.im-meta-option {
  background: #fff;
  border-color: #e5e5e5;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.im-meta-option:hover {
  border-color: rgba(0, 174, 239, 0.25);
  background: #f5fbfe;
}
.im-meta-option-checked {
  background: #f5fbfe;
  border-color: #00aeef;
}
.im-meta-value {
  color: #00aeef;
}

/* Select */
.im-select {
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 6px 8px;
  background: #fff;
  outline: none;
  color: #333;
}
.im-select:focus {
  border-color: #00aeef;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.15);
}
.im-select:disabled {
  background: #fafafa;
  color: #777;
}

/* Checkbox */
.im-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 15px;
  height: 15px;
  margin: 0;
  background: #fff;
  border: 1.5px solid #e5e5e5;
  border-radius: 3px;
  cursor: pointer;
  vertical-align: middle;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.im-checkbox:hover:not(:disabled) {
  border-color: #00aeef;
}
.im-checkbox:checked {
  background: #00aeef;
  border-color: #00aeef;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2 6l3 3 5-5' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 11px 11px;
}
.im-checkbox:disabled {
  background: #fafafa;
  border-color: #e5e5e5;
  cursor: not-allowed;
  opacity: 0.6;
}
.im-checkbox:checked:disabled {
  background: #00aeef;
  border-color: #00aeef;
  opacity: 0.5;
}
.im-checkbox:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 174, 239, 0.25);
}

/* Buttons */
.im-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  line-height: 1.3;
  min-height: 28px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
}
.im-btn-outline {
  padding: 8px 16px;
  font-size: 13px;
  border-radius: 6px;
  background: #fff;
  border-color: #e5e5e5;
  color: #333;
}
.im-btn-outline:hover {
  background: #f5fbfe;
  border-color: #00aeef;
  color: #00aeef;
}
.im-btn-secondary {
  background: #fff;
  border-color: #e5e5e5;
  color: #333;
}
.im-btn-secondary:hover {
  background: #f5fbfe;
  border-color: #00aeef;
  color: #00aeef;
}
.im-btn-ghost {
  background: transparent;
  border-color: transparent;
  color: #777;
  padding-left: 0;
}
.im-btn-ghost:hover {
  color: #00aeef;
}
.im-btn-primary {
  background: #00aeef;
  border-color: #00aeef;
  color: #fff;
}
.im-btn-primary:hover:not(:disabled) {
  background: #0098d4;
  border-color: #0098d4;
}
.im-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Spinner */
.im-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: im-spin 0.6s linear infinite;
}
@keyframes im-spin {
  to { transform: rotate(360deg); }
}
</style>
